/**
 * FinanceReportScreen — Unified screen for all financial reports
 * All data from T_AccountDetails (single source of truth)
 *
 * Reports:
 *  - gl-ledger (2.10.1): Single account transactions with running balance
 *  - daily-summary (2.10.4): Day-by-day balance
 *  - monthly-summary (2.10.5): Month-by-month balance
 *  - multi-ledger (2.10.2): Multiple accounts grouped
 *  - trial-balance (2.10.9 / 2.11.1): All accounts aggregated
 *  - pdc-maturity (2.12.2): Matured PDC cheques
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useGlobal } from '../../store/GlobalStoreProvider';
import { GK } from '../../store/GlobalKeys';
import { Search, ChevronRight, ChevronDown } from 'lucide-react';
import FinanceReportGrid from '../../components/erp/FinanceReportGrid';
import ERPAdvancedGrid from '../../components/erp/ERPAdvancedGrid';
import LazyTree from '../../components/erp/LazyTree';
import VoucherScreen from './VoucherScreen';
import { erpToast } from '../../components/erp/ERPToast';
import '../../styles/erp-theme.css';
import { API_BASE as API, getToken} from '../../lib/api';

const tkn = () => getToken() || '';
const hdrs = () => ({ Authorization: `Bearer ${tkn()}`, 'Content-Type': 'application/json' });

const autoSelect = (e) => e.target.select();

// ═══════════════════════════════════════════════════════
// GL LEDGER REPORT (2.10.1) — Modern Accountant Layout
// Left Panel (Ledger Tree) + Header (Filters + KPI Tiles) + ERPAdvancedGrid
// ═══════════════════════════════════════════════════════
const GLLedgerReport = ({ initialAccountId, initialAccountName, initialFromDate, initialToDate } = {}) => {
  // ── Deep-link: read URL query params on initial render ──
  // Priority: sessionStorage prefilter (from "Open Ledger" action) > URL params > props > defaults
  const [initState] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const ss = (() => { try { const v = JSON.parse(sessionStorage.getItem('glLedgerPrefilter') || '{}'); if (v.accountId) sessionStorage.removeItem('glLedgerPrefilter'); return v; } catch { return {}; } })();
      return {
        accountId:   ss.accountId   || p.get('accountId')   || initialAccountId   || '',
        accountName: ss.accountName || decodeURIComponent(p.get('accountName') || '') || initialAccountName || '',
        fromDate:    p.get('fromDate')  || initialFromDate  || '2025-04-01',
        toDate:      p.get('toDate')    || initialToDate    || '2026-03-31',
      };
    } catch { return { accountId: initialAccountId || '', accountName: initialAccountName || '', fromDate: initialFromDate || '2025-04-01', toDate: initialToDate || '2026-03-31' }; }
  });
  const [accountId, setAccountId] = useState(initState.accountId);
  const [accountName, setAccountName] = useState(initState.accountName);
  const [branchId, setBranchId] = useState('');
  const [fromDate, setFromDate] = useState(initState.fromDate);
  const [toDate, setToDate] = useState(initState.toDate);
  const [voucherType, setVoucherType] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [treeSearch, setTreeSearch] = useState('');
  const [recentLedgers, setRecentLedgers] = useState([]);
  const [voucherEdit, setVoucherEdit] = useState(null);
  // Grid state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const [filters, setFilters] = useState({});

  const fmtC = (v) => (v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });

  useEffect(() => {
    (async () => {
      try { const r = await fetch(`${API}/api/finance-reports/accounts`, { headers: hdrs() }); const j = await r.json(); if (j.success) setAccounts(j.data || []); } catch {}
      try { const r = await fetch(`${API}/api/finance-reports/branches`, { headers: hdrs() }); const j = await r.json(); if (j.success) setBranches(j.data || []); } catch {}
    })();
  }, []);

  const loadReport = useCallback(async () => {
    if (!accountId) return;
    setLoading(true); setPage(1);
    try {
      const r = await fetch(`${API}/api/finance-reports/gl-ledger?accountId=${accountId}&branchId=${branchId}&fromDate=${fromDate}&toDate=${toDate}&fiscalYear=2025-2026`, { headers: hdrs() });
      const j = await r.json();
      if (j.success) setData(j.data);
    } catch { erpToast.error('Failed to load report'); }
    setLoading(false);
  }, [accountId, branchId, fromDate, toDate]);

  useEffect(() => { if (accountId) loadReport(); }, [accountId, branchId, fromDate, toDate]);

  const selectLedger = (glId, glName) => {
    setAccountId(glId); setAccountName(glName);
    setRecentLedgers(prev => { const f = prev.filter(r => r.id !== glId); return [{ id: glId, name: glName }, ...f].slice(0, 5); });
  };

  // ── Deep-link: keep URL in sync so users can copy/share ──
  useEffect(() => {
    if (!accountId) return;
    try {
      const p = new URLSearchParams(window.location.search);
      p.set('accountId', accountId);
      p.set('accountName', encodeURIComponent(accountName));
      p.set('fromDate', fromDate);
      p.set('toDate', toDate);
      window.history.replaceState({}, '', `${window.location.pathname}?${p.toString()}`);
    } catch {}
  }, [accountId, accountName, fromDate, toDate]);

  // Filter entries by voucherType
  const filteredEntries = useMemo(() => {
    let items = data?.entries || [];
    if (voucherType) items = items.filter(e => e.TransType === voucherType);
    Object.entries(filters).forEach(([k, v]) => { if (v) items = items.filter(r => String(r[k] || '').toLowerCase().includes(v.toLowerCase())); });
    if (sortField) items = [...items].sort((a, b) => { const av = String(a[sortField] || ''), bv = String(b[sortField] || ''); return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av); });
    return items;
  }, [data, voucherType, filters, sortField, sortDir]);

  const pageData = filteredEntries.slice((page - 1) * pageSize, page * pageSize);

  // KPI calculations
  const kpi = useMemo(() => {
    if (!data) return { openBal: '0.00', totalDr: 0, totalCr: 0, closeBal: '0.00', count: 0, avg: 0 };
    const count = filteredEntries.length;
    const totalDr = filteredEntries.reduce((s, e) => s + (e.DrAmount || 0), 0);
    const totalCr = filteredEntries.reduce((s, e) => s + (e.CrAmount || 0), 0);
    return { openBal: data.openingBalance?.balance || '0.00', totalDr, totalCr, closeBal: data.closingBalance?.balance || '0.00', count, avg: count > 0 ? (totalDr + totalCr) / count : 0 };
  }, [data, filteredEntries]);

  const COLUMNS = [
    { field: 'TransDateStr', header: 'Date' },
    { field: 'TransNo', header: 'Voucher No' },
    { field: 'TransType', header: 'Type' },
    { field: 'AccountName', header: 'Ledger' },
    { field: 'DrAmount', header: 'Debit' },
    { field: 'CrAmount', header: 'Credit' },
    { field: 'Balance', header: 'Balance' },
    { field: 'TranDetails', header: 'Narration' },
    { field: 'DocRef', header: 'Ref No' },
  ];

  return (
    <div style={{ display: 'flex', height: '100%' }} data-testid="gl-ledger-report">
      {/* ── LEFT PANEL: LazyTree ── */}
      <div style={{ width: 250, flexShrink: 0, borderRight: '1px solid var(--erp-border)', display: 'flex', flexDirection: 'column', background: '#fafbfc' }}>
        <div style={{ padding: '8px', borderBottom: '1px solid var(--erp-border)' }}>
          <div style={{ position: 'relative' }}>
            <Search size={12} style={{ position: 'absolute', left: 6, top: 7, color: '#9ca3af' }} />
            <input value={treeSearch} onChange={e => setTreeSearch(e.target.value)} placeholder="Search ledger..." className="erp-input" style={{ paddingLeft: 24, fontSize: 11, height: 28, width: '100%' }} />
          </div>
        </div>
        {recentLedgers.length > 0 && (
          <div style={{ padding: '6px 8px', borderBottom: '1px solid var(--erp-border)' }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: '#6b7280', marginBottom: 3, textTransform: 'uppercase', letterSpacing: 1 }}>Recent</div>
            {recentLedgers.map(r => (
              <div key={r.id} onClick={() => selectLedger(r.id, r.name)} style={{ fontSize: 12, padding: '4px 6px', cursor: 'pointer', borderRadius: 4, background: r.id === accountId ? '#dbeafe' : 'transparent', color: r.id === accountId ? '#1d4ed8' : '#333', fontWeight: r.id === accountId ? 700 : 500 }}>{r.name}</div>
            ))}
          </div>
        )}
        <LazyTree onSelect={(node) => selectLedger(node.id, node.name)} selectedId={accountId} searchText={treeSearch} />
        <div style={{ padding: '4px 8px', borderTop: '1px solid var(--erp-border)', fontSize: 9, color: '#9ca3af' }}>{accounts.length} ledgers</div>
      </div>

      {/* ── RIGHT: Header + KPI + Grid ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Filters Row — compact pill style */}
        <div style={{ padding: '6px 12px', borderBottom: '1px solid var(--erp-border)', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', position: 'sticky', top: 0, zIndex: 5, background: 'var(--erp-surface)' }}>
          {/* #1: Selected ledger name — big font, left side */}
          <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--erp-primary)', marginRight: 'auto', minWidth: 120 }}>{accountName || 'Select a Ledger'}</span>
          {/* #2: All controls right-aligned */}
          {accountId && (
            <button
              title="Copy shareable link"
              className="erp-btn erp-btn-ghost"
              style={{ height: 24, fontSize: 10, padding: '0 8px', borderRadius: 12 }}
              onClick={() => { navigator.clipboard?.writeText(window.location.href); erpToast.success('Link copied — share or bookmark it!'); }}
              data-testid="gl-copy-link-btn"
            >Share Link</button>
          )}
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="erp-input" style={{ height: 24, fontSize: 10, width: 110, borderRadius: 12 }} title="From Date" />
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="erp-input" style={{ height: 24, fontSize: 10, width: 110, borderRadius: 12 }} title="To Date" />
          <select value={branchId} onChange={e => setBranchId(e.target.value)} className="erp-input erp-select" style={{ height: 24, fontSize: 10, width: 110, borderRadius: 12 }}><option value="">All Branches</option>{branches.map(b => <option key={b.ID} value={b.ID}>{b.Name}</option>)}</select>
          <select value={voucherType} onChange={e => setVoucherType(e.target.value)} className="erp-input erp-select" style={{ height: 24, fontSize: 10, width: 100, borderRadius: 12 }}><option value="">All Types</option>{['CR','CP','BR','BP','JV','PV','SV','DN','CN'].map(t => <option key={t} value={t}>{t}</option>)}</select>
          <button onClick={loadReport} className="erp-btn erp-btn-primary" style={{ height: 24, fontSize: 10, padding: '0 12px', borderRadius: 12 }}>Apply</button>
          <button onClick={() => { setFromDate('2025-04-01'); setToDate('2026-03-31'); setBranchId(''); setVoucherType(''); }} className="erp-btn erp-btn-ghost" style={{ height: 24, fontSize: 10, padding: '0 8px', borderRadius: 12 }}>Reset</button>
        </div>

        {/* KPI Tiles */}
        {data && (
          <div style={{ display: 'flex', gap: 8, padding: '8px 12px', borderBottom: '1px solid var(--erp-border)', background: 'var(--erp-surface-alt)' }}>
            {[
              { label: 'Opening Balance', value: kpi.openBal, color: '#3498db' },
              { label: 'Total Debit', value: fmtC(kpi.totalDr), color: '#e74c3c' },
              { label: 'Total Credit', value: fmtC(kpi.totalCr), color: '#27ae60' },
              { label: 'Closing Balance', value: kpi.closeBal, color: '#2c3e50' },
              { label: 'Transactions', value: kpi.count, color: '#8e44ad' },
              { label: 'Avg Value', value: fmtC(kpi.avg), color: '#f39c12' },
            ].map(tile => (
              <div key={tile.label} style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--erp-border)', background: 'var(--erp-surface)', minWidth: 0 }}>
                <div style={{ fontSize: 9, color: 'var(--erp-text-muted)', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tile.label}</div>
                <div style={{ fontSize: 14, fontWeight: 700, fontFamily: 'var(--erp-font-mono)', color: tile.color, marginTop: 2 }}>{tile.value}</div>
              </div>
            ))}
          </div>
        )}

        {/* Main Grid */}
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <ERPAdvancedGrid
            title=""
            columns={COLUMNS}
            data={pageData}
            total={filteredEntries.length}
            idField="TransNo"
            nameField="AccountName"
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={s => { setPageSize(s); setPage(1); }}
            sortField={sortField}
            sortDir={sortDir}
            onSort={f => { if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortField(f); setSortDir('asc'); } setPage(1); }}
            filters={filters}
            onFilterChange={f => { setFilters(f); setPage(1); }}
            loading={loading}
            showAddNew={false}
            testId="gl-ledger"
            exportFileName={`GL_${accountId}`}
            onEdit={(row, mode) => {
              const typeMap = { CR: 'cash-receipts', CP: 'cash-payments', BR: 'bank-receipts', BP: 'bank-payments', JV: 'journal-vouchers', PV: 'cash-payments', SV: 'cash-receipts', DN: 'journal-vouchers', CN: 'journal-vouchers' };
              setVoucherEdit({ voucherType: typeMap[row.TransType] || 'cash-receipts', docId: row.TransNo, mode: mode || 'view' });
            }}
            cellRenderer={(field, value) => {
              if (field === 'DrAmount' && value > 0) return <span style={{ fontFamily: 'var(--erp-font-mono)', fontSize: 11, color: '#e74c3c' }}>{fmtC(value)}</span>;
              if (field === 'CrAmount' && value > 0) return <span style={{ fontFamily: 'var(--erp-font-mono)', fontSize: 11, color: '#27ae60' }}>{fmtC(value)}</span>;
              if (field === 'DrAmount' || field === 'CrAmount') return <span style={{ color: '#ccc' }}>-</span>;
              if (field === 'TransType') return <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 3, background: value === 'CR' ? '#e8f8ef' : value === 'CP' ? '#fdeceb' : value === 'BR' ? '#ebf5fb' : value === 'BP' ? '#fef5e7' : '#f0f0f0', color: value === 'CR' ? '#27ae60' : value === 'CP' ? '#e74c3c' : value === 'BR' ? '#3498db' : value === 'BP' ? '#f39c12' : '#666' }}>{value}</span>;
              return null;
            }}
            cellClassName={field => ['DrAmount', 'CrAmount', 'Balance'].includes(field) ? 'text-right' : ''}
          />
        </div>
      </div>

      {/* Voucher View/Edit Modal — in-page (RULE 32) */}
      {voucherEdit && (
        <div className="erp-modal-overlay" style={{ zIndex: 1300 }} data-testid="voucher-edit-modal">
          <div style={{ position: 'relative', width: '95vw', maxWidth: 1200, height: '90vh', background: 'var(--erp-surface)', borderRadius: 'var(--erp-radius)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 12px', borderBottom: '1px solid var(--erp-border)', background: 'var(--erp-primary)', color: '#fff' }}>
              <span style={{ fontWeight: 700, fontSize: 13 }}>Voucher — {voucherEdit.docId} ({voucherEdit.mode})</span>
              <button onClick={() => setVoucherEdit(null)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 16, fontWeight: 700 }}>X</button>
            </div>
            <div style={{ flex: 1, overflow: 'auto' }}>
              <VoucherScreen voucherType={voucherEdit.voucherType} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ═══════════════════════════════════════════════════════
// TRIAL BALANCE (2.10.9 / 2.11.1)
// ═══════════════════════════════════════════════════════
const TrialBalanceReport = () => {
  const companyName = useGlobal(GK.COMPANY_NAME, 'Company');
  const [branchId, setBranchId] = useState('');
  const [withZeros, setWithZeros] = useState(false);
  const [jvDate, setJvDate] = useState('2026-04-01');
  const [columnMode, setColumnMode] = useState(2);
  const [displayFormat, setDisplayFormat] = useState('horizontal');
  const [viewMode, setViewMode] = useState('flat');
  const [data, setData] = useState(null);
  const [treeData, setTreeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);

  useEffect(() => { (async () => { try { const r = await fetch(`${API}/api/finance-reports/branches`, { headers: hdrs() }); const j = await r.json(); if (j.success) setBranches(j.data || []); } catch {} })(); }, []);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/finance-reports/trial-balance?fiscalYear=2025-2026&branchId=${branchId}&withZeros=${withZeros}&jvDate=${jvDate}`, { headers: hdrs() });
      const j = await r.json();
      if (j.success) setData(j.data);
      // Load tree for levels view
      if (viewMode === 'levels') {
        const r2 = await fetch(`${API}/api/financial-statements/financial-statement?category=all&branchId=${branchId}`, { headers: hdrs() });
        const j2 = await r2.json();
        if (j2.success) setTreeData(j2.data);
      }
    } catch { erpToast.error('Failed to load'); }
    setLoading(false);
  }, [branchId, withZeros, jvDate, viewMode]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const fmtC = (v) => (v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });

  const columns = useMemo(() => {
    const base = [{ field: 'AccountId', header: 'Account Id', width: 100, mono: true }, { field: 'AccountName', header: 'Account Name', width: 220 }];
    if (columnMode === 6) { base.push({ field: 'DrOpen', header: 'Dr Opening', align: 'right', mono: true, isCurrency: true, width: 110 }, { field: 'CrOpen', header: 'Cr Opening', align: 'right', mono: true, isCurrency: true, width: 110 }, { field: 'DrCurrent', header: 'Dr Current', align: 'right', mono: true, isCurrency: true, width: 110 }, { field: 'CrCurrent', header: 'Cr Current', align: 'right', mono: true, isCurrency: true, width: 110 }); }
    else if (columnMode === 4) { base.push({ field: 'DrOpen', header: 'Dr Opening', align: 'right', mono: true, isCurrency: true, width: 120 }, { field: 'CrOpen', header: 'Cr Opening', align: 'right', mono: true, isCurrency: true, width: 120 }); }
    base.push({ field: 'Debit', header: 'Debit', align: 'right', mono: true, isCurrency: true, width: 120 }, { field: 'Credit', header: 'Credit', align: 'right', mono: true, isCurrency: true, width: 120 }, { field: 'ClosingBalance', header: 'Closing Balance', align: 'right', mono: true, width: 130 });
    return base;
  }, [columnMode]);

  const filterContent = (
    <div className="voucher-header-grid" style={{ gap: '0 24px' }}>
      <div className="erp-form-row"><label className="erp-form-label">Company</label><div className="erp-form-control"><input value={companyName} readOnly className="erp-input" style={{ background: 'var(--erp-hover)', fontWeight: 600 }} /></div></div>
      <div className="erp-form-row"><label className="erp-form-label">JV Date</label><div className="erp-form-control"><input type="date" value={jvDate} onChange={e => setJvDate(e.target.value)} className="erp-input" /></div></div>
      <div className="erp-form-row"><label className="erp-form-label">Columns</label><div className="erp-form-control"><select value={columnMode} onChange={e => setColumnMode(parseInt(e.target.value))} className="erp-input erp-select"><option value={2}>2 Col (Closing)</option><option value={4}>4 Col (Opening+Closing)</option><option value={6}>6 Col (Open+Current+Close)</option></select></div></div>
      <div className="erp-form-row"><label className="erp-form-label">Format</label><div className="erp-form-control"><select value={displayFormat} onChange={e => setDisplayFormat(e.target.value)} className="erp-input erp-select"><option value="horizontal">Horizontal</option><option value="vertical">Vertical (T-Format)</option></select></div></div>
      <div className="erp-form-row"><label className="erp-form-label">Branch</label><div className="erp-form-control"><select value={branchId} onChange={e => setBranchId(e.target.value)} className="erp-input erp-select"><option value="">Main Branch</option>{branches.map(b => <option key={b.ID} value={b.ID}>{b.Name}</option>)}</select></div></div>
      <div className="erp-form-row"><label className="erp-form-label">View / Filter</label><div className="erp-form-control" style={{ display: 'flex', gap: 8 }}>
        <select value={viewMode} onChange={e => setViewMode(e.target.value)} className="erp-input erp-select" style={{ width: 90 }}><option value="flat">Flat</option><option value="levels">Levels</option></select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11 }}><input type="radio" name="zeros" checked={!withZeros} onChange={() => setWithZeros(false)} /> No Zeros</label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11 }}><input type="radio" name="zeros" checked={withZeros} onChange={() => setWithZeros(true)} /> With Zeros</label>
      </div></div>
    </div>
  );

  // Vertical T-Format
  if (displayFormat === 'vertical' && data) {
    const accounts = data.accounts || [];
    const drAccounts = accounts.filter(a => (a.Debit || 0) > (a.Credit || 0));
    const crAccounts = accounts.filter(a => (a.Credit || 0) > (a.Debit || 0));
    return (
      <div className="erp-module" data-testid="trial-balance">
        <div className="erp-screen-header">
          <span className="erp-screen-title">Trial Balance</span>
          <span style={{ fontSize: 12, fontWeight: 600, marginLeft: 16, color: data.isBalanced ? 'var(--erp-success)' : 'var(--erp-danger)' }}>Accounts: {data.total} | {data.isBalanced ? 'BALANCED' : 'NOT BALANCED'}</span>
          <div className="erp-screen-actions"><button className="erp-btn erp-btn-ghost" onClick={() => window.print()}>Print</button></div>
        </div>
        <div style={{ padding: 'var(--erp-sp-3)', borderBottom: '1px solid var(--erp-border)' }}>{filterContent}</div>
        <div style={{ flex: 1, display: 'flex', gap: 1, overflow: 'auto' }}>
          <div style={{ flex: 1, borderRight: '2px solid var(--erp-primary)' }}>
            <div style={{ padding: '6px 12px', background: 'var(--erp-primary)', color: '#fff', fontWeight: 700, fontSize: 13, textAlign: 'center' }}>Debit</div>
            <div style={{ overflow: 'auto' }}>{drAccounts.map(a => (
              <div key={a.AccountId} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 12px', borderBottom: '1px solid var(--erp-border)', fontSize: 11 }}>
                <span>{a.AccountName}</span>
                <span style={{ fontFamily: 'var(--erp-font-mono)' }}>{fmtC((a.Debit || 0) - (a.Credit || 0))}</span>
              </div>
            ))}<div style={{ padding: '6px 12px', background: 'var(--erp-primary-light)', fontWeight: 700, fontSize: 12, textAlign: 'right', fontFamily: 'var(--erp-font-mono)' }}>Total: {fmtC(data.totalDr)}</div></div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ padding: '6px 12px', background: 'var(--erp-primary)', color: '#fff', fontWeight: 700, fontSize: 13, textAlign: 'center' }}>Credit</div>
            <div style={{ overflow: 'auto' }}>{crAccounts.map(a => (
              <div key={a.AccountId} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 12px', borderBottom: '1px solid var(--erp-border)', fontSize: 11 }}>
                <span>{a.AccountName}</span>
                <span style={{ fontFamily: 'var(--erp-font-mono)' }}>{fmtC((a.Credit || 0) - (a.Debit || 0))}</span>
              </div>
            ))}<div style={{ padding: '6px 12px', background: 'var(--erp-primary-light)', fontWeight: 700, fontSize: 12, textAlign: 'right', fontFamily: 'var(--erp-font-mono)' }}>Total: {fmtC(data.totalCr)}</div></div>
          </div>
        </div>
      </div>
    );
  }

  return <FinanceReportGrid title="Trial Balance" columns={columns} data={data?.accounts || []} loading={loading} headerContent={filterContent} testId="trial-balance" exportFileName="TrialBalance" summaryLine={data ? `Accounts: ${data.total} | Dr: ${data.totalDr?.toLocaleString('en-IN',{minimumFractionDigits:2})} | Cr: ${data.totalCr?.toLocaleString('en-IN',{minimumFractionDigits:2})} | ${data.isBalanced ? 'BALANCED' : 'NOT BALANCED'}` : ''} />;
};


// ═══════════════════════════════════════════════════════
// DAILY SUMMARY (2.10.4)
// ═══════════════════════════════════════════════════════
const DailySummaryReport = () => {
  const [accountId, setAccountId] = useState('');
  const [branchId, setBranchId] = useState('');
  const [month, setMonth] = useState('April');
  const [fromDate, setFromDate] = useState('2025-04-01');
  const [toDate, setToDate] = useState('2025-04-30');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [branches, setBranches] = useState([]);

  useEffect(() => { (async () => { try { const r = await fetch(`${API}/api/finance-reports/accounts`, { headers: hdrs() }); const j = await r.json(); if (j.success) setAccounts(j.data || []); } catch {} try { const r = await fetch(`${API}/api/finance-reports/branches`, { headers: hdrs() }); const j = await r.json(); if (j.success) setBranches(j.data || []); } catch {} })(); }, []);

  const loadReport = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/finance-reports/daily-summary?accountId=${accountId}&branchId=${branchId}&fromDate=${fromDate}&toDate=${toDate}&fiscalYear=2025-2026`, { headers: hdrs() });
      const j = await r.json();
      if (j.success) setData(j.data);
    } catch { erpToast.error('Failed to load'); }
    setLoading(false);
  }, [accountId, branchId, fromDate, toDate]);

  useEffect(() => { if (accountId) loadReport(); }, [accountId, branchId, fromDate, toDate]);

  const columns = [
    { field: 'Particulars', header: 'Particulars', width: 120 },
    { field: 'OpeningBalance', header: 'Opening Balance', align: 'right', mono: true, width: 150 },
    { field: 'DebitBal', header: 'Debit Bal', align: 'right', mono: true, isCurrency: true, width: 130 },
    { field: 'CreditBal', header: 'Credit Bal', align: 'right', mono: true, isCurrency: true, width: 130 },
    { field: 'ClosingBalance', header: 'Closing Balance', align: 'right', mono: true, width: 150 },
  ];

  const footerRows = data ? [
    { label: 'Totals:', values: { DebitBal: data.totals?.dr || 0, CreditBal: data.totals?.cr || 0, ClosingBalance: (Math.abs(data.closingBalance || 0).toFixed(2) + ((data.closingBalance || 0) >= 0 ? ' Dr' : ' Cr')) } },
  ] : [];

  const filterContent = (
    <div className="voucher-header-grid" style={{ gap: '0 24px' }}>
      <div className="erp-form-row"><label className="erp-form-label required">Account Name</label><div className="erp-form-control"><select value={accountId} onChange={e => setAccountId(e.target.value)} className="erp-input erp-select" data-testid="daily-account"><option value="">--Select Account--</option>{accounts.map(a => <option key={a.GLId} value={a.GLId}>{a.GLName}</option>)}</select></div></div>
      <div className="erp-form-row"><label className="erp-form-label">Branch</label><div className="erp-form-control"><select value={branchId} onChange={e => setBranchId(e.target.value)} className="erp-input erp-select"><option value="">Main Branch</option>{branches.map(b => <option key={b.ID} value={b.ID}>{b.Name}</option>)}</select></div></div>
      <div className="erp-form-row"><label className="erp-form-label">Select Month</label><div className="erp-form-control"><select value={month} onChange={e => setMonth(e.target.value)} className="erp-input erp-select">{['April','May','June','July','August','September','October','November','December','January','February','March'].map(m => <option key={m} value={m}>{m}</option>)}</select></div></div>
      <div className="erp-form-row"><label className="erp-form-label">From / To Date</label><div className="erp-form-control" style={{ display: 'flex', gap: 6 }}><input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="erp-input" style={{ flex: 1 }} /><input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="erp-input" style={{ flex: 1 }} /></div></div>
    </div>
  );

  return <FinanceReportGrid title="Daily Balance Summary" columns={columns} data={data?.days || []} footerRows={footerRows} loading={loading} headerContent={filterContent} testId="daily-summary" exportFileName="DailySummary" />;
};


// ═══════════════════════════════════════════════════════
// MONTHLY SUMMARY (2.10.5)
// ═══════════════════════════════════════════════════════
const MonthlySummaryReport = () => {
  const [accountId, setAccountId] = useState('');
  const [branchId, setBranchId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [branches, setBranches] = useState([]);

  useEffect(() => { (async () => { try { const r = await fetch(`${API}/api/finance-reports/accounts`, { headers: hdrs() }); const j = await r.json(); if (j.success) setAccounts(j.data || []); } catch {} try { const r = await fetch(`${API}/api/finance-reports/branches`, { headers: hdrs() }); const j = await r.json(); if (j.success) setBranches(j.data || []); } catch {} })(); }, []);

  const loadReport = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/finance-reports/monthly-summary?accountId=${accountId}&branchId=${branchId}&fiscalYear=2025-2026`, { headers: hdrs() });
      const j = await r.json();
      if (j.success) setData(j.data);
    } catch { erpToast.error('Failed to load'); }
    setLoading(false);
  }, [accountId, branchId]);

  useEffect(() => { if (accountId) loadReport(); }, [accountId, branchId]);

  const columns = [
    { field: 'MonthName', header: 'Month', width: 120 },
    { field: 'OpeningBalance', header: 'Opening Balance', align: 'right', mono: true, isCurrency: true, width: 150 },
    { field: 'DebitBal', header: 'Debit Bal', align: 'right', mono: true, isCurrency: true, width: 130 },
    { field: 'CreditBal', header: 'Credit Bal', align: 'right', mono: true, isCurrency: true, width: 130 },
    { field: 'ClosingBalance', header: 'Closing Balance', align: 'right', mono: true, isCurrency: true, width: 150 },
  ];

  const filterContent = (
    <div className="voucher-header-grid" style={{ gap: '0 24px' }}>
      <div className="erp-form-row"><label className="erp-form-label required">Account Name</label><div className="erp-form-control"><select value={accountId} onChange={e => setAccountId(e.target.value)} className="erp-input erp-select" data-testid="monthly-account"><option value="">--Select Account--</option>{accounts.map(a => <option key={a.GLId} value={a.GLId}>{a.GLName}</option>)}</select></div></div>
      <div className="erp-form-row"><label className="erp-form-label">Branch</label><div className="erp-form-control"><select value={branchId} onChange={e => setBranchId(e.target.value)} className="erp-input erp-select"><option value="">Main Branch</option>{branches.map(b => <option key={b.ID} value={b.ID}>{b.Name}</option>)}</select></div></div>
    </div>
  );

  return <FinanceReportGrid title="Monthly Balance Summary" columns={columns} data={data?.months || []} loading={loading} headerContent={filterContent} testId="monthly-summary" exportFileName="MonthlySummary" />;
};


// ═══════════════════════════════════════════════════════
// PDC MATURITY (2.12.2)
// ═══════════════════════════════════════════════════════
const PDCMaturityReport = () => {
  const [type, setType] = useState('all');
  const [getDate, setGetDate] = useState('This Year');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/api/finance-reports/pdc-maturity?type=${type}`;
      if (fromDate) url += `&fromDate=${fromDate}`;
      if (toDate) url += `&toDate=${toDate}`;
      const r = await fetch(url, { headers: hdrs() });
      const j = await r.json();
      if (j.success) setData(j.data);
    } catch { erpToast.error('Failed to load'); }
    setLoading(false);
  }, [type, fromDate, toDate]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const columns = [
    { field: 'SlNo', header: 'SlNo', width: 60 },
    { field: 'DocId', header: 'DocId', width: 140, mono: true },
    { field: 'DocDate', header: 'DocDate', width: 100 },
    { field: 'ChequeNo', header: 'ChequeNo', width: 100 },
    { field: 'MaturityDate', header: 'MaturityDate', width: 100 },
    { field: 'ChequeAmount', header: 'ChequeAmount', align: 'right', mono: true, isCurrency: true, width: 130 },
  ];

  const filterContent = (
    <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', gap: 12 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}><input type="radio" name="pdctype" checked={type==='receipts'} onChange={() => setType('receipts')} /> Receipts</label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}><input type="radio" name="pdctype" checked={type==='payments'} onChange={() => setType('payments')} /> Payments</label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}><input type="radio" name="pdctype" checked={type==='all'} onChange={() => setType('all')} /> ALL</label>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
        <span>Get Date</span>
        <select value={getDate} onChange={e => setGetDate(e.target.value)} className="erp-input erp-select" style={{ width: 120, fontSize: 11 }}><option>This Year</option><option>This Month</option><option>This Week</option><option>Custom</option></select>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
        <span>From Date</span><input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="erp-input" style={{ fontSize: 11, width: 130 }} />
        <span>To Date</span><input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="erp-input" style={{ fontSize: 11, width: 130 }} />
      </div>
    </div>
  );

  return <FinanceReportGrid title="Matured PDC Vouchers" columns={columns} data={data?.vouchers || []} loading={loading} headerContent={filterContent} testId="pdc-maturity" exportFileName="PDC_Maturity" summaryLine={data ? `Total Count: ${data.totalCount}  Total Amount: ${data.totalAmount?.toLocaleString('en-IN',{minimumFractionDigits:2})}` : ''} />;
};


// ═══════════════════════════════════════════════════════
// MULTIPLE LEDGER REPORT (2.10.2)
// Reuses GL Report patterns: filters, actions, export, voucher modal
// ═══════════════════════════════════════════════════════
const MultiLedgerReport = () => {
  const [selectedIds, setSelectedIds] = useState(new Map());
  const [branchId, setBranchId] = useState('');
  const [fromDate, setFromDate] = useState('2025-04-01');
  const [toDate, setToDate] = useState('2026-03-31');
  const [voucherType, setVoucherType] = useState('');
  const [globalSearch, setGlobalSearch] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [treeSearch, setTreeSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [voucherEdit, setVoucherEdit] = useState(null);

  useEffect(() => { (async () => { try { const r = await fetch(`${API}/api/finance-reports/branches`, { headers: hdrs() }); const j = await r.json(); if (j.success) setBranches(j.data || []); } catch {} })(); }, []);

  const fmtC = (v) => (v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });

  const handleMultiSelect = (id, name) => {
    setSelectedIds(prev => { const n = new Map(prev); if (n.has(id)) n.delete(id); else n.set(id, name); return n; });
  };

  const loadReport = useCallback(async () => {
    if (selectedIds.size === 0) { erpToast.warning('Select at least one ledger'); return; }
    setLoading(true); setPage(1);
    try {
      const ids = Array.from(selectedIds.keys()).join(',');
      const r = await fetch(`${API}/api/finance-reports/multi-ledger?accountIds=${ids}&branchId=${branchId}&fromDate=${fromDate}&toDate=${toDate}&fiscalYear=2025-2026`, { headers: hdrs() });
      const j = await r.json();
      if (j.success) setData(j.data);
    } catch { erpToast.error('Failed to load report'); }
    setLoading(false);
  }, [selectedIds, branchId, fromDate, toDate]);

  const handleReset = () => {
    setSelectedIds(new Map()); setData(null); setBranchId('');
    setFromDate('2025-04-01'); setToDate('2026-03-31');
    setVoucherType(''); setGlobalSearch(''); setPage(1);
  };

  // Build flat rows with filters applied
  const flatRows = useMemo(() => {
    if (!data?.ledgers) return [];
    const rows = [];
    for (const ledger of data.ledgers) {
      let entries = ledger.entries || [];
      if (voucherType) entries = entries.filter(e => e.TransType === voucherType);
      if (globalSearch) { const s = globalSearch.toLowerCase(); entries = entries.filter(e => (e.TransNo || '').toLowerCase().includes(s) || (e.TranDetails || '').toLowerCase().includes(s) || (e.DocRef || '').toLowerCase().includes(s)); }
      if (entries.length === 0 && !voucherType && !globalSearch) {
        rows.push({ _type: 'header', accountId: ledger.accountId, accountName: ledger.accountName, opening: ledger.openingBalance, entryCount: 0 });
        rows.push({ _type: 'subtotal', accountName: ledger.accountName, ...ledger.totals });
      } else if (entries.length > 0) {
        rows.push({ _type: 'header', accountId: ledger.accountId, accountName: ledger.accountName, opening: ledger.openingBalance, entryCount: entries.length });
        for (const entry of entries) { rows.push({ _type: 'entry', ...entry, _ledgerName: ledger.accountName }); }
        const fDr = entries.reduce((s, e) => s + (e.DrAmount || 0), 0);
        const fCr = entries.reduce((s, e) => s + (e.CrAmount || 0), 0);
        rows.push({ _type: 'subtotal', accountName: ledger.accountName, dr: (ledger.openingBalance?.dr || 0) + fDr, cr: (ledger.openingBalance?.cr || 0) + fCr, closing: ledger.totals.closing });
      }
    }
    if (rows.filter(r => r._type === 'header').length > 1) {
      const grandDr = rows.filter(r => r._type === 'subtotal').reduce((s, r) => s + (r.dr || 0), 0);
      const grandCr = rows.filter(r => r._type === 'subtotal').reduce((s, r) => s + (r.cr || 0), 0);
      rows.push({ _type: 'grand', dr: grandDr, cr: grandCr });
    }
    return rows;
  }, [data, voucherType, globalSearch]);

  const pageRows = flatRows.slice((page - 1) * pageSize, page * pageSize);
  const totalPages = Math.ceil(flatRows.length / pageSize) || 1;
  const selectedIdsSet = useMemo(() => new Set(selectedIds.keys()), [selectedIds]);

  // Export ALL ledgers (no 20 limit for export)
  const handleExport = (format) => {
    const allEntryRows = flatRows.filter(r => r._type !== 'grand');
    const csvHeader = 'Ledger,Date,Type,VoucherNo,Debit,Credit,Balance,Narration,Ref\n';
    let currentLedger = '';
    const csvRows = allEntryRows.map(r => {
      if (r._type === 'header') { currentLedger = r.accountName; return `"--- ${r.accountName} (Opening: ${r.opening?.balance || '0.00'}) ---",,,,,,,,`; }
      if (r._type === 'subtotal') return `"Subtotal: ${r.accountName}",,,"${fmtC(r.dr)}","${fmtC(r.cr)}","${r.closing}",,`;
      return `"${currentLedger}","${r.TransDateStr || ''}","${r.TransType || ''}","${r.TransNo || ''}","${r.DrAmount || 0}","${r.CrAmount || 0}","${r.Balance || ''}","${(r.TranDetails || '').replace(/"/g, '""')}","${r.DocRef || ''}"`;
    }).join('\n');

    if (format === 'excel' || format === 'csv') {
      const blob = new Blob(['\ufeff' + csvHeader + csvRows], { type: format === 'excel' ? 'application/vnd.ms-excel' : 'text/csv' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `MultiLedger_${data?.ledgerCount || 0}_ledgers.${format === 'excel' ? 'xls' : 'csv'}`; a.click();
    } else {
      window.print();
    }
    erpToast.success(`Exported ${allEntryRows.length} rows (${data?.ledgerCount || 0} ledgers)`);
  };

  return (
    <div style={{ display: 'flex', height: '100%' }} data-testid="multi-ledger-report">
      {/* Left: LazyTree multi-select */}
      <div style={{ width: 250, flexShrink: 0, borderRight: '1px solid var(--erp-border)', display: 'flex', flexDirection: 'column', background: '#fafbfc' }}>
        <div style={{ padding: 8, borderBottom: '1px solid var(--erp-border)' }}>
          <div style={{ position: 'relative' }}><Search size={12} style={{ position: 'absolute', left: 6, top: 7, color: '#9ca3af' }} /><input value={treeSearch} onChange={e => setTreeSearch(e.target.value)} placeholder="Search ledger..." className="erp-input" style={{ paddingLeft: 24, fontSize: 11, height: 28, width: '100%' }} /></div>
        </div>
        {selectedIds.size > 0 && (
          <div style={{ padding: '4px 8px', borderBottom: '1px solid var(--erp-border)', fontSize: 11, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#3b82f6', fontWeight: 700 }}>{selectedIds.size} selected</span>
            <button onClick={() => setSelectedIds(new Map())} style={{ fontSize: 10, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}>Clear all</button>
          </div>
        )}
        <LazyTree multiSelect selectedIds={selectedIdsSet} onMultiSelect={handleMultiSelect} onSelect={() => {}} selectedId="" searchText={treeSearch} />
        <div style={{ padding: '6px 8px', borderTop: '1px solid var(--erp-border)' }}>
          <button onClick={loadReport} className="erp-btn erp-btn-primary" style={{ width: '100%', fontSize: 11 }} disabled={selectedIds.size === 0 || loading}>Load Report ({selectedIds.size})</button>
        </div>
      </div>

      {/* Right: Filters + Grid */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Filter bar — reuses GL Report pattern */}
        <div style={{ padding: '6px 12px', borderBottom: '1px solid var(--erp-border)', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', position: 'sticky', top: 0, zIndex: 5, background: 'var(--erp-surface)' }}>
          <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--erp-primary)', marginRight: 'auto' }}>Multiple Ledger Report</span>
          {/* Global search */}
          <div style={{ position: 'relative' }}><Search size={11} style={{ position: 'absolute', left: 6, top: 7, color: '#9ca3af' }} /><input value={globalSearch} onChange={e => { setGlobalSearch(e.target.value); setPage(1); }} placeholder="Search all..." className="erp-input" style={{ paddingLeft: 24, height: 24, fontSize: 10, width: 140, borderRadius: 12 }} /></div>
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="erp-input" style={{ height: 24, fontSize: 10, width: 115, borderRadius: 12 }} />
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="erp-input" style={{ height: 24, fontSize: 10, width: 115, borderRadius: 12 }} />
          <select value={branchId} onChange={e => setBranchId(e.target.value)} className="erp-input erp-select" style={{ height: 24, fontSize: 10, width: 100, borderRadius: 12 }}><option value="">All Branches</option>{branches.map(b => <option key={b.ID} value={b.ID}>{b.Name}</option>)}</select>
          <select value={voucherType} onChange={e => { setVoucherType(e.target.value); setPage(1); }} className="erp-input erp-select" style={{ height: 24, fontSize: 10, width: 80, borderRadius: 12 }}><option value="">All Types</option>{['CR','CP','BR','BP','JV','PV','SV','DN','CN'].map(t => <option key={t} value={t}>{t}</option>)}</select>
          <button onClick={loadReport} className="erp-btn erp-btn-primary" style={{ height: 24, fontSize: 10, padding: '0 10px', borderRadius: 12 }}>Apply</button>
          <button onClick={handleReset} className="erp-btn erp-btn-ghost" style={{ height: 24, fontSize: 10, padding: '0 8px', borderRadius: 12 }}>Reset</button>
          {/* Export dropdown */}
          <div style={{ position: 'relative' }}>
            <button onClick={() => { const el = document.getElementById('ml-export-dd'); if (el) el.style.display = el.style.display === 'block' ? 'none' : 'block'; }} className="erp-btn erp-btn-ghost" style={{ height: 24, fontSize: 10, padding: '0 8px', borderRadius: 12 }}>Export</button>
            <div id="ml-export-dd" style={{ display: 'none', position: 'absolute', right: 0, top: '100%', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 100, minWidth: 130 }}>
              <button onClick={() => { handleExport('csv'); document.getElementById('ml-export-dd').style.display = 'none'; }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 12px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 11 }}>Export CSV</button>
              <button onClick={() => { handleExport('excel'); document.getElementById('ml-export-dd').style.display = 'none'; }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 12px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 11 }}>Export Excel</button>
              <button onClick={() => { handleExport('pdf'); document.getElementById('ml-export-dd').style.display = 'none'; }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 12px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 11 }}>Print / PDF</button>
            </div>
          </div>
        </div>

        {/* KPI summary when data loaded */}
        {data && (
          <div style={{ display: 'flex', gap: 8, padding: '6px 12px', borderBottom: '1px solid var(--erp-border)', background: '#f8fafc' }}>
            <div style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid #e5e7eb', fontSize: 11 }}><span style={{ color: '#6b7280' }}>Ledgers: </span><span style={{ fontWeight: 700, color: '#8e44ad' }}>{data.ledgerCount}</span></div>
            <div style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid #e5e7eb', fontSize: 11 }}><span style={{ color: '#6b7280' }}>Total Dr: </span><span style={{ fontWeight: 700, color: '#e74c3c', fontFamily: 'var(--erp-font-mono)' }}>{fmtC(data.grandTotal.dr)}</span></div>
            <div style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid #e5e7eb', fontSize: 11 }}><span style={{ color: '#6b7280' }}>Total Cr: </span><span style={{ fontWeight: 700, color: '#27ae60', fontFamily: 'var(--erp-font-mono)' }}>{fmtC(data.grandTotal.cr)}</span></div>
            <div style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid #e5e7eb', fontSize: 11 }}><span style={{ color: '#6b7280' }}>Rows: </span><span style={{ fontWeight: 700 }}>{flatRows.filter(r => r._type === 'entry').length}</span></div>
          </div>
        )}

        {/* Grid */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          <table className="erp-grid" data-testid="multi-ledger-grid" style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}><tr><th style={{ width: 30 }}>Act</th><th>Date</th><th>Type</th><th>Voucher No</th><th style={{ textAlign: 'right' }}>Debit</th><th style={{ textAlign: 'right' }}>Credit</th><th style={{ textAlign: 'right' }}>Balance</th><th>Narration</th><th>Ref</th></tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={9} style={{ padding: 48, textAlign: 'center', color: '#9ca3af' }}>Loading...</td></tr>
              : flatRows.length === 0 ? <tr><td colSpan={9} style={{ padding: 48, textAlign: 'center', color: '#9ca3af' }}>Select ledgers and click "Load Report"</td></tr>
              : pageRows.map((row, i) => {
                if (row._type === 'header') return (
                  <tr key={`h-${i}`} style={{ background: '#e0ecf8', fontWeight: 700 }}>
                    <td colSpan={6} style={{ padding: '8px 12px', fontSize: 13, color: '#1e40af' }}>{row.accountName} <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 400 }}>({row.accountId}) — {row.entryCount} entries</span></td>
                    <td colSpan={3} style={{ textAlign: 'right', padding: '8px 12px', fontFamily: 'var(--erp-font-mono)', fontSize: 12 }}>Opening: {row.opening?.balance || '0.00'}</td>
                  </tr>
                );
                if (row._type === 'subtotal') return (
                  <tr key={`s-${i}`} style={{ background: '#d1dce8', fontWeight: 700 }}>
                    <td /><td colSpan={3} style={{ padding: '6px 12px', fontSize: 12, fontStyle: 'italic' }}>Subtotal: {row.accountName}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 12, padding: '6px 8px' }}>{fmtC(row.dr)}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 12, padding: '6px 8px' }}>{fmtC(row.cr)}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 12, padding: '6px 8px', fontWeight: 700 }}>{row.closing}</td>
                    <td colSpan={2} />
                  </tr>
                );
                if (row._type === 'grand') return (
                  <tr key={`g-${i}`} style={{ background: '#3b5998', color: '#fff', fontWeight: 700 }}>
                    <td /><td colSpan={3} style={{ padding: '8px 12px', fontSize: 13 }}>GRAND TOTAL</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 13, padding: '8px' }}>{fmtC(row.dr)}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 13, padding: '8px' }}>{fmtC(row.cr)}</td>
                    <td colSpan={3} />
                  </tr>
                );
                // Entry row with Actions
                const typeMap = { CR: 'cash-receipts', CP: 'cash-payments', BR: 'bank-receipts', BP: 'bank-payments', JV: 'journal-vouchers', PV: 'cash-payments', SV: 'cash-receipts', DN: 'journal-vouchers', CN: 'journal-vouchers' };
                return (
                  <tr key={`e-${i}`}>
                    <td style={{ textAlign: 'center' }}><button onClick={() => setVoucherEdit({ voucherType: typeMap[row.TransType] || 'cash-receipts', docId: row.TransNo, mode: 'view' })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 11 }} title="View Voucher">...</button></td>
                    <td style={{ fontSize: 11 }}>{row.TransDateStr}</td>
                    <td><span style={{ fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 3, background: row.TransType === 'CR' ? '#e8f8ef' : row.TransType === 'CP' ? '#fdeceb' : row.TransType === 'BR' ? '#ebf5fb' : row.TransType === 'BP' ? '#fef5e7' : '#f0f0f0', color: row.TransType === 'CR' ? '#27ae60' : row.TransType === 'CP' ? '#e74c3c' : row.TransType === 'BR' ? '#3498db' : row.TransType === 'BP' ? '#f39c12' : '#666' }}>{row.TransType}</span></td>
                    <td style={{ fontSize: 11, fontFamily: 'var(--erp-font-mono)' }}>{row.TransNo}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 11, color: row.DrAmount > 0 ? '#e74c3c' : '#ccc' }}>{row.DrAmount > 0 ? fmtC(row.DrAmount) : '-'}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 11, color: row.CrAmount > 0 ? '#27ae60' : '#ccc' }}>{row.CrAmount > 0 ? fmtC(row.CrAmount) : '-'}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 11 }}>{row.Balance}</td>
                    <td style={{ fontSize: 10 }}>{row.TranDetails || ''}</td>
                    <td style={{ fontSize: 10 }}>{row.DocRef || ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="erp-pagination">
          <span>{flatRows.length > 0 ? `${(page-1)*pageSize+1} to ${Math.min(page*pageSize, flatRows.length)} from ${flatRows.length} rows (${data?.ledgerCount || 0} ledgers)` : '0 rows'}</span>
          <div className="erp-pagination-controls">
            <span>Rows</span><select value={pageSize} onChange={e => { setPageSize(parseInt(e.target.value)); setPage(1); }}>{[50,100,250,500].map(s => <option key={s} value={s}>{s}</option>)}</select>
            <button className="erp-pagination-btn" onClick={() => setPage(1)} disabled={page <= 1}>&laquo;</button>
            <button className="erp-pagination-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>&lsaquo;</button>
            <span>Page {page} of {totalPages}</span>
            <button className="erp-pagination-btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>&rsaquo;</button>
            <button className="erp-pagination-btn" onClick={() => setPage(totalPages)} disabled={page >= totalPages}>&raquo;</button>
          </div>
        </div>
      </div>

      {/* Voucher Modal — reused from GL Report (RULE 32) */}
      {voucherEdit && (
        <div className="erp-modal-overlay" style={{ zIndex: 1300 }} data-testid="voucher-edit-modal">
          <div style={{ position: 'relative', width: '95vw', maxWidth: 1200, height: '90vh', background: 'var(--erp-surface)', borderRadius: 'var(--erp-radius)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 12px', borderBottom: '1px solid var(--erp-border)', background: 'var(--erp-primary)', color: '#fff' }}>
              <span style={{ fontWeight: 700, fontSize: 13 }}>Voucher — {voucherEdit.docId}</span>
              <button onClick={() => setVoucherEdit(null)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 16, fontWeight: 700 }}>X</button>
            </div>
            <div style={{ flex: 1, overflow: 'auto' }}><VoucherScreen voucherType={voucherEdit.voucherType} /></div>
          </div>
        </div>
      )}
    </div>
  );
};


// ═══════════════════════════════════════════════════════
// GROUP LEDGER SUMMARY (2.10.7)
// Aggregated per-ledger view. Drilldown → opens GL Report for that ledger.
// ═══════════════════════════════════════════════════════
const GroupSummaryReport = () => {
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [branchId, setBranchId] = useState('');
  const [fromDate, setFromDate] = useState('2025-04-01');
  const [toDate, setToDate] = useState('2026-03-31');
  const [showOpening, setShowOpening] = useState(false);
  const [globalSearch, setGlobalSearch] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [treeSearch, setTreeSearch] = useState('');
  // Drilldown state — opens GL Report inline
  const [drillLedger, setDrillLedger] = useState(null);

  useEffect(() => { (async () => { try { const r = await fetch(`${API}/api/finance-reports/branches`, { headers: hdrs() }); const j = await r.json(); if (j.success) setBranches(j.data || []); } catch {} })(); }, []);

  const fmtC = (v) => (v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });

  const handleGroupSelect = (node) => {
    if (node.type === 'group' || !node.hasChildren) return; // Only select groups (non-leaf expandable)
    setSelectedGroupId(node.id);
    setDrillLedger(null);
  };

  const loadReport = useCallback(async () => {
    if (!selectedGroupId) { erpToast.warning('Select a group from the tree'); return; }
    setLoading(true); setDrillLedger(null);
    try {
      const r = await fetch(`${API}/api/finance-reports/group-summary?groupId=${selectedGroupId}&branchId=${branchId}&fromDate=${fromDate}&toDate=${toDate}&fiscalYear=2025-2026`, { headers: hdrs() });
      const j = await r.json();
      if (j.success) setData(j.data);
    } catch { erpToast.error('Failed to load'); }
    setLoading(false);
  }, [selectedGroupId, branchId, fromDate, toDate]);

  useEffect(() => { if (selectedGroupId) loadReport(); }, [selectedGroupId, branchId, fromDate, toDate]);

  const handleReset = () => { setSelectedGroupId(''); setData(null); setBranchId(''); setFromDate('2025-04-01'); setToDate('2026-03-31'); setGlobalSearch(''); setShowOpening(false); setDrillLedger(null); };

  const filteredLedgers = useMemo(() => {
    if (!data?.ledgers) return [];
    if (!globalSearch) return data.ledgers;
    const s = globalSearch.toLowerCase();
    return data.ledgers.filter(l => l.AccountName.toLowerCase().includes(s) || l.AccountId.toLowerCase().includes(s));
  }, [data, globalSearch]);

  const handleExport = (format) => {
    const hdr = showOpening ? 'AccountId,AccountName,OpenDr,OpenCr,DrAmount,CrAmount,ClosingBalance\n' : 'AccountId,AccountName,DrAmount,CrAmount,ClosingBalance\n';
    const rows = filteredLedgers.map(l => showOpening ? `"${l.AccountId}","${l.AccountName}",${l.DrOpen},${l.CrOpen},${l.DrAmount},${l.CrAmount},"${l.ClosingBalance}"` : `"${l.AccountId}","${l.AccountName}",${l.DrAmount},${l.CrAmount},"${l.ClosingBalance}"`).join('\n');
    if (format === 'pdf') { window.print(); return; }
    const blob = new Blob(['\ufeff' + hdr + rows], { type: format === 'excel' ? 'application/vnd.ms-excel' : 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `GroupSummary_${data?.groupName || ''}.${format === 'excel' ? 'xls' : 'csv'}`; a.click();
    erpToast.success(`Exported ${filteredLedgers.length} ledgers`);
  };

  // If drilldown active, show GL Report for that ledger
  if (drillLedger) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ padding: '6px 12px', borderBottom: '1px solid var(--erp-border)', display: 'flex', alignItems: 'center', gap: 8, background: '#f8fafc' }}>
          <button onClick={() => setDrillLedger(null)} className="erp-btn erp-btn-ghost" style={{ fontSize: 11 }}>← Back to {data?.groupName || 'Group Summary'}</button>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--erp-primary)' }}>{drillLedger.name} ({drillLedger.id})</span>
        </div>
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <GLLedgerReport initialAccountId={drillLedger.id} initialAccountName={drillLedger.name} />
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', height: '100%' }} data-testid="group-summary-report">
      {/* Left: LazyTree — select ONE group (checkbox on groups, not leaves) */}
      <div style={{ width: 250, flexShrink: 0, borderRight: '1px solid var(--erp-border)', display: 'flex', flexDirection: 'column', background: '#fafbfc' }}>
        <div style={{ padding: 8, borderBottom: '1px solid var(--erp-border)' }}>
          <div style={{ position: 'relative' }}><Search size={12} style={{ position: 'absolute', left: 6, top: 7, color: '#9ca3af' }} /><input value={treeSearch} onChange={e => setTreeSearch(e.target.value)} placeholder="Search group..." className="erp-input" style={{ paddingLeft: 24, fontSize: 11, height: 28, width: '100%' }} /></div>
        </div>
        {selectedGroupId && <div style={{ padding: '4px 8px', borderBottom: '1px solid var(--erp-border)', fontSize: 11, color: '#3b82f6', fontWeight: 700 }}>Group: {data?.groupName || selectedGroupId}</div>}
        <LazyTree onSelect={(node) => { if (node.type === 'group') setSelectedGroupId(node.id); }} selectedId={selectedGroupId} searchText={treeSearch} />
      </div>

      {/* Right */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Filters */}
        <div style={{ padding: '6px 12px', borderBottom: '1px solid var(--erp-border)', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', background: 'var(--erp-surface)' }}>
          <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--erp-primary)', marginRight: 'auto' }}>{data?.groupName || 'Group Ledger Summary'}</span>
          <div style={{ position: 'relative' }}><Search size={11} style={{ position: 'absolute', left: 6, top: 7, color: '#9ca3af' }} /><input value={globalSearch} onChange={e => setGlobalSearch(e.target.value)} placeholder="Search..." className="erp-input" style={{ paddingLeft: 24, height: 24, fontSize: 10, width: 130, borderRadius: 12 }} /></div>
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="erp-input" style={{ height: 24, fontSize: 10, width: 115, borderRadius: 12 }} />
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="erp-input" style={{ height: 24, fontSize: 10, width: 115, borderRadius: 12 }} />
          <select value={branchId} onChange={e => setBranchId(e.target.value)} className="erp-input erp-select" style={{ height: 24, fontSize: 10, width: 100, borderRadius: 12 }}><option value="">All Branches</option>{branches.map(b => <option key={b.ID} value={b.ID}>{b.Name}</option>)}</select>
          <button onClick={() => setShowOpening(p => !p)} className={`erp-btn ${showOpening ? 'erp-btn-primary' : 'erp-btn-ghost'}`} style={{ height: 24, fontSize: 10, padding: '0 8px', borderRadius: 12 }}>{showOpening ? 'Hide Opening' : 'Show Opening'}</button>
          <button onClick={loadReport} className="erp-btn erp-btn-primary" style={{ height: 24, fontSize: 10, padding: '0 10px', borderRadius: 12 }}>Apply</button>
          <button onClick={handleReset} className="erp-btn erp-btn-ghost" style={{ height: 24, fontSize: 10, padding: '0 8px', borderRadius: 12 }}>Reset</button>
          <div style={{ position: 'relative' }}>
            <button onClick={() => { const el = document.getElementById('gs-export-dd'); if (el) el.style.display = el.style.display === 'block' ? 'none' : 'block'; }} className="erp-btn erp-btn-ghost" style={{ height: 24, fontSize: 10, padding: '0 8px', borderRadius: 12 }}>Export</button>
            <div id="gs-export-dd" style={{ display: 'none', position: 'absolute', right: 0, top: '100%', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 100, minWidth: 120 }}>
              {['csv','excel','pdf'].map(f => <button key={f} onClick={() => { handleExport(f); document.getElementById('gs-export-dd').style.display = 'none'; }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 12px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 11 }}>{f.toUpperCase()}</button>)}
            </div>
          </div>
        </div>

        {/* KPI */}
        {data && (
          <div style={{ display: 'flex', gap: 8, padding: '6px 12px', borderBottom: '1px solid var(--erp-border)', background: '#f8fafc' }}>
            <div style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid #e5e7eb', fontSize: 11 }}><span style={{ color: '#6b7280' }}>Ledgers: </span><span style={{ fontWeight: 700 }}>{data.total}</span></div>
            <div style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid #e5e7eb', fontSize: 11 }}><span style={{ color: '#6b7280' }}>Dr: </span><span style={{ fontWeight: 700, color: '#e74c3c', fontFamily: 'var(--erp-font-mono)' }}>{fmtC(data.totalDr)}</span></div>
            <div style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid #e5e7eb', fontSize: 11 }}><span style={{ color: '#6b7280' }}>Cr: </span><span style={{ fontWeight: 700, color: '#27ae60', fontFamily: 'var(--erp-font-mono)' }}>{fmtC(data.totalCr)}</span></div>
          </div>
        )}

        {/* Summary Grid — one row per ledger, click to drilldown */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          <table className="erp-grid" data-testid="group-summary-grid" style={{ width: '100%' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}><tr>
              <th style={{ width: 100 }}>Account ID</th>
              <th>Account Name</th>
              {showOpening && <><th style={{ textAlign: 'right', width: 120 }}>Open Dr</th><th style={{ textAlign: 'right', width: 120 }}>Open Cr</th></>}
              <th style={{ textAlign: 'right', width: 130 }}>Dr Amount</th>
              <th style={{ textAlign: 'right', width: 130 }}>Cr Amount</th>
              <th style={{ textAlign: 'right', width: 140 }}>Closing Balance</th>
              <th style={{ width: 50 }}>Txns</th>
            </tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={showOpening ? 8 : 6} style={{ padding: 48, textAlign: 'center', color: '#9ca3af' }}>Loading...</td></tr>
              : !data ? <tr><td colSpan={showOpening ? 8 : 6} style={{ padding: 48, textAlign: 'center', color: '#9ca3af' }}>Select a group from the tree</td></tr>
              : filteredLedgers.length === 0 ? <tr><td colSpan={showOpening ? 8 : 6} style={{ padding: 48, textAlign: 'center', color: '#9ca3af' }}>No ledgers in this group</td></tr>
              : filteredLedgers.map((l, i) => (
                <tr key={l.AccountId} onClick={() => setDrillLedger({ id: l.AccountId, name: l.AccountName })} style={{ cursor: 'pointer' }} className={i % 2 === 0 ? '' : 'bg-slate-50'} data-testid={`gs-row-${l.AccountId}`}>
                  <td style={{ fontFamily: 'var(--erp-font-mono)', fontSize: 11 }}>{l.AccountId}</td>
                  <td style={{ fontSize: 12, fontWeight: 500, color: '#1e40af' }}>{l.AccountName}</td>
                  {showOpening && <><td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 11 }}>{fmtC(l.DrOpen)}</td><td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 11 }}>{fmtC(l.CrOpen)}</td></>}
                  <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 12, color: '#e74c3c' }}>{fmtC(l.DrAmount)}</td>
                  <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 12, color: '#27ae60' }}>{fmtC(l.CrAmount)}</td>
                  <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 12, fontWeight: 700 }}>{l.ClosingBalance}</td>
                  <td style={{ textAlign: 'center', fontSize: 10, color: '#6b7280' }}>{l.TxnCount}</td>
                </tr>
              ))}
              {/* Totals row */}
              {data && filteredLedgers.length > 0 && (
                <tr style={{ background: '#3b5998', color: '#fff', fontWeight: 700 }}>
                  <td />
                  <td style={{ padding: '8px 12px', fontSize: 13 }}>TOTAL ({filteredLedgers.length} ledgers)</td>
                  {showOpening && <><td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 12, padding: '8px' }}>{fmtC(data.totalOpenDr)}</td><td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 12, padding: '8px' }}>{fmtC(data.totalOpenCr)}</td></>}
                  <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 13, padding: '8px' }}>{fmtC(data.totalDr)}</td>
                  <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 13, padding: '8px' }}>{fmtC(data.totalCr)}</td>
                  <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 13, padding: '8px' }}>{fmtC(Math.abs((data.totalOpenDr+data.totalDr)-(data.totalOpenCr+data.totalCr)))} {((data.totalOpenDr+data.totalDr)-(data.totalOpenCr+data.totalCr)) >= 0 ? 'Dr' : 'Cr'}</td>
                  <td />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════
// MAIN: Routes by reportType prop
// ═══════════════════════════════════════════════════════
const FinanceReportScreen = ({ theme = 'light', reportType = 'gl-ledger' }) => {
  switch (reportType) {
    case 'trial-balance': return <TrialBalanceReport />;
    case 'daily-summary': return <DailySummaryReport />;
    case 'monthly-summary': return <MonthlySummaryReport />;
    case 'pdc-maturity': return <PDCMaturityReport />;
    case 'multi-ledger': return <MultiLedgerReport />;
    case 'group-summary': return <GroupSummaryReport />;
    case 'gl-ledger':
    default: return <GLLedgerReport />;
  }
};

export default FinanceReportScreen;
